import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Resource, ResourceLanguage } from 'i18next';

// ─── Auto-discover all locale files (SC-006: adding a new language requires only
//     dropping JSON files in src/locales/{locale}/ — no code changes needed) ───
//
// Public pages (landing, /product, legal) must not download the whole authenticated app's
// copy. Only the namespaces in the eager glob below ship in the entry bundle; every other
// namespace is its own chunk, loaded by `ensureLocales()` before a route that needs it
// renders (see `lazyWithLocales` in main.tsx). Tests load everything synchronously.
// Keep the brace list below in sync (Vite needs a literal glob).

const PATH_PATTERN = /\.\.\/locales\/([^/]+)\/([^/]+)\.json$/;
const isTest = import.meta.env.MODE === 'test';

const eagerModules = import.meta.glob(
  '../locales/*/{common,validation,marketing,showcase}.json',
  { eager: true },
);
const allEagerModules = isTest ? import.meta.glob('../locales/**/*.json', { eager: true }) : {};
const lazyModules = import.meta.glob('../locales/**/*.json');

type JsonModule = { default: ResourceLanguage } | ResourceLanguage;
const unwrap = (mod: unknown): ResourceLanguage =>
  (mod as { default?: ResourceLanguage }).default ?? (mod as ResourceLanguage);

// Build the i18next resources object from the bundled files
const resources: Resource = {};
for (const [path, mod] of Object.entries({ ...eagerModules, ...allEagerModules })) {
  // path format: '../locales/en/common.json' → locale='en', ns='common'
  const match = PATH_PATTERN.exec(path);
  if (match?.[1] && match[2]) {
    const locale = match[1];
    const ns = match[2];
    if (!resources[locale]) resources[locale] = {} as ResourceLanguage;
    (resources[locale])[ns] = unwrap(mod as JsonModule);
  }
}

export const NAMESPACES = [
  'common',
  'auth',
  'contacts',
  'companies',
  'deals',
  'tasks',
  'calendar',
  'tickets',
  'dashboard',
  'getStarted',
  'invoices',
  'email',
  'reports',
  'settings',
  'validation',
  'prospects',
  'booking-widgets',
  'public-booking',
  'stays',
  'campaigns',
  'quotes',
  'products',
  'notifications',
  'sequences',
  'integrations',
  'ai',
  'analytics',
  'organization',
  'social',
  'workflows',
  'segments',
  'knowledgeBase',
  'helpDocs',
  'publicHelp',
  'meetings',
  'landing-pages',
  'public-quote',
  'public-landing',
  'leads',
  'platformAdmin',
  'showcase',
  'marketing',
  'billing',
  'legal',
  'privacy',
  'designSystem',
] as const;

const LOCALE_STORAGE_KEY = 'freelancecrm-locale';

// Read persisted locale synchronously so the initial render uses the right language.
// Falls back to 'sv' for unauthenticated / first-time visitors.
const initialLng = (typeof localStorage !== 'undefined' && localStorage.getItem(LOCALE_STORAGE_KEY)) || 'sv';

void i18next.use(initReactI18next).init({
  resources,
  lng: initialLng,
  fallbackLng: 'sv',
  defaultNS: 'common',
  ns: [...NAMESPACES],
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false,
  },
});

// Keep <html lang> on the active language for screen readers and crawlers.
// index.html ships `sv` (the default locale) for the first paint.
function syncHtmlLang(lng: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = lng.split('-')[0] || 'sv';
}
syncHtmlLang(initialLng);
i18next.on('languageChanged', syncHtmlLang);

// Once a route has asked for the full app copy, a language switch must load it for the new
// language before the switch, or the screen would show raw keys until the chunk arrives.
let appLocalesRequested = false;
const changeLanguageNow = i18next.changeLanguage.bind(i18next);
i18next.changeLanguage = async (lng, callback) => {
  if (appLocalesRequested && lng) {
    try {
      await ensureLocales(undefined, lng);
    } catch {
      /* keep the current language usable; the switch still happens and eager namespaces render */
    }
  }
  return changeLanguageNow(lng, callback);
};

const inflight = new Map<string, Promise<void>>();
const MAX_LOAD_RETRIES = 2;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Runs one lazy locale chunk load, shared between concurrent callers. A failed load is retried
 * up to MAX_LOAD_RETRIES times with a short backoff and is never kept in `inflight`, so a later
 * call (e.g. the user pressing Retry) starts a fresh attempt instead of replaying the rejection.
 */
export function loadLocaleJob(
  key: string,
  load: () => Promise<unknown>,
  apply: (mod: unknown) => void,
  baseDelayMs = 300,
): Promise<void> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const job = (async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        apply(await load());
        return;
      } catch (err) {
        if (attempt >= MAX_LOAD_RETRIES) throw err;
        await sleep(baseDelayMs * (attempt + 1));
      }
    }
  })().finally(() => inflight.delete(key));
  inflight.set(key, job);
  return job;
}

/**
 * Loads locale namespaces that are not in the entry bundle (all of them when `namespaces`
 * is omitted) for the active language (or `language`). Safe to call repeatedly. In the
 * production build all of one language's lazy namespaces share one chunk (vite.config.ts).
 */
export function ensureLocales(namespaces?: readonly string[], language?: string): Promise<void> {
  if (!namespaces) appLocalesRequested = true;
  const lng = (language ?? i18next.language ?? initialLng).split('-')[0];
  const jobs: Promise<void>[] = [];
  for (const [path, load] of Object.entries(lazyModules)) {
    const match = PATH_PATTERN.exec(path);
    const locale = match?.[1];
    const ns = match?.[2];
    if (!locale || !ns) continue;
    // Only the active language: the other one is fetched when the user switches (see below).
    if (locale !== lng) continue;
    if (namespaces && !namespaces.includes(ns)) continue;
    if (i18next.hasResourceBundle(locale, ns)) continue;
    const job = loadLocaleJob(
      path,
      load,
      (mod) => {
        i18next.addResourceBundle(locale, ns, unwrap(mod), true, false);
      },
    );
    jobs.push(job);
  }
  return Promise.all(jobs).then(() => undefined);
}

export default i18next;
